/**
 * Driver documents: when each one ends, the 14-day warning, and the sentence a driver ticks to say the documents are theirs and true.
 * A document ends at the END of its printed date in Lagos time (UTC+1, no daylight saving): a licence that ends on the 12th is valid until 23:59:59 on the 12th.
 */
export const DOC_KEYS = ['lic', 'rw', 'ins'] as const;
export type DocKey = (typeof DOC_KEYS)[number];
export const DOC_NAMES: Record<DocKey, string> = { lic: 'Driver’s licence', rw: 'Roadworthiness certificate', ins: 'Insurance certificate' };
export const DOC_WARN_DAYS = 14;
const DAY_MS = 86_400_000, LAGOS_MS = 3_600_000;

/** The moment a document stops being valid: the end of its date in Lagos. NaN when the text is not a date. */
export function docEndMs(isoDate: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return NaN;
  return Date.parse(`${isoDate}T23:59:59+01:00`);
}
/** Today's date in Lagos as YYYY-MM-DD. */
export const lagosDay = (now: number): string => new Date(now + LAGOS_MS).toISOString().slice(0, 10);

export type DocState = 'soon' | 'today' | 'expired';
export interface DocNotice { key: DocKey; name: string; date: string; state: DocState; days: number }

/** What needs the driver's attention now: expired, ending today, or ending within 14 days. Documents that are fine, or with no date, are left out. */
export function docNotices(exp: Partial<Record<DocKey, string | null | undefined>>, now: number): DocNotice[] {
  const today = Date.parse(lagosDay(now) + 'T00:00:00Z'), out: DocNotice[] = [];
  for (const key of DOC_KEYS) {
    const date = exp[key];
    if (!date || Number.isNaN(docEndMs(date))) continue;
    const days = Math.round((Date.parse(date + 'T00:00:00Z') - today) / DAY_MS);
    if (days < 0) out.push({ key, name: DOC_NAMES[key], date, state: 'expired', days });
    else if (days === 0) out.push({ key, name: DOC_NAMES[key], date, state: 'today', days });
    else if (days <= DOC_WARN_DAYS) out.push({ key, name: DOC_NAMES[key], date, state: 'soon', days });
  }
  return out;
}
export const docsExpired = (exp: Partial<Record<DocKey, string | null | undefined>>, now: number): DocNotice[] => docNotices(exp, now).filter((n) => n.state === 'expired');

/** The words a driver ticks when they upload documents. The server stores this exact text with the date and the version, so the version must change whenever the words do. */
export const DOC_CERT = {
  version: 1,
  text: 'I certify that the documents and dates I uploaded are genuine, current and mine, and that they are true, accurate and complete. I accept full responsibility for their correctness. I understand that false information can lead to my account being suspended, and that I must update them before they expire. I agree KonkRide may store them to run the service.',
} as const;
